import React from 'react';
import { ITag } from '@fluentui/react';
import { IGenericFilterTag } from '../../utils/types/IGenericFilterTag';
import ListFilters from '../common/lists/ListFilters';
import CompanyCollapsibleChecklist from '../common/formFields/CompanyCollapsibleChecklist';
import CountryCollapsibleChecklist from '../common/formFields/CountryCollapsibleChecklist';
import PeoplePickerCollapsibleChecklist from '../common/formFields/PeoplePickerCollapsibleChecklist';
import GenericCollapsibleChecklist from '../common/formFields/GenericCollapsibleChecklist';
import BooleanCollapsibleDropdown from '../common/formFields/BooleanCollapsibleDropdown';
import GET_ENTITY_STATUSES_QUERY, {
  GET_LIFECYCLE_STATUSES_QUERY,
  GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
  GET_LEGO_CATEGORIES_QUERY,
} from '../../utils/statApi/EntityStatusApi';
import IDropdownReference from '../../utils/types/IDropdownReference';

export interface IMonitoringQueueFiltersProps {
  onClosePanel?: () => void;
  currentFilterValues?: IGenericFilterTag[];
  onFilterUpdate?: (formData: IGenericFilterTag[]) => void;
}

const generateFiscalYearTags = (): ITag[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];

  for (let i = currentYear + 2; i >= currentYear - 9; i -= 1) {
    years.push(i);
  }

  return years.map((fy) => ({
    key: `FY${String(fy).slice(-2)}`,
    name: `FY${String(fy).slice(-2)}`,
  }));
};

const mapEntityStatuses = (data: { entityStatuses: IDropdownReference[] }): ITag[] =>
  data?.entityStatuses?.map((status) => ({
    key: String(status.id),
    name: status.fieldName ?? '',
  })) ?? [];

const mapLifecycleStatuses = (data: { lifecycleStatuses: IDropdownReference[] }): ITag[] =>
  data?.lifecycleStatuses?.map((status) => ({
    key: String(status.id),
    name: status.fieldName ?? '',
  })) ?? [];

const mapTerminationLifecycleStatuses = (data: {
  terminationLifecycleStatuses: IDropdownReference[];
}): ITag[] =>
  data?.terminationLifecycleStatuses
    ?.filter((status) => status.fieldName?.trim() !== '--')
    .map((status) => ({
      key: String(status.id),
      name: status.fieldName ?? '',
    })) ?? [];

const mapLegoCategories = (data: { legoCategories: IDropdownReference[] }): ITag[] =>
  data?.legoCategories?.map((category) => ({
    key: String(category.id),
    name: category.fieldName ?? '',
  })) ?? [];

const MonitoringQueueFilters = (props: IMonitoringQueueFiltersProps): JSX.Element => {
  const { onClosePanel, currentFilterValues, onFilterUpdate } = props;

  return (
    <ListFilters
      onClosePanel={onClosePanel}
      onFilterUpdate={onFilterUpdate}
      currentFilterValues={currentFilterValues}
    >
      <CompanyCollapsibleChecklist label="Company" dataKey="company" />
      <CountryCollapsibleChecklist label="Country" dataKey="country" />
      <PeoplePickerCollapsibleChecklist label="STAT Controller" dataKey="controller" />
      <PeoplePickerCollapsibleChecklist label="Termination Contact" dataKey="terminationContact" />
      <PeoplePickerCollapsibleChecklist label="CELA Term Contact" dataKey="celaTermContact" />
      <PeoplePickerCollapsibleChecklist
        label="Business Stakeholder Contact"
        dataKey="businessStakeholderContact"
      />
      <GenericCollapsibleChecklist
        label="Anticipated Termination FY"
        dataKey="anticipatedTerminationFiscalYear"
        options={generateFiscalYearTags()}
        showSearch={false}
      />
      <GenericCollapsibleChecklist
        label="Legal Status"
        dataKey="entityStatusIds"
        query={GET_ENTITY_STATUSES_QUERY}
        mapData={mapEntityStatuses}
        showSearch={false}
      />
      <GenericCollapsibleChecklist
        label="Lifecycle Status"
        dataKey="lifecycleStatusIds"
        query={GET_LIFECYCLE_STATUSES_QUERY}
        mapData={mapLifecycleStatuses}
        showSearch={false}
      />
      <GenericCollapsibleChecklist
        label="Termination Lifecycle Status"
        dataKey="terminationLifecycleStatusId"
        query={GET_TERMINATION_LIFECYCLE_STATUSES_QUERY}
        mapData={mapTerminationLifecycleStatuses}
        showSearch={false}
      />
      <BooleanCollapsibleDropdown
        label="Legal In-Process Follow-Up Required"
        dataKey="legalInProcessFollowUpRequired"
      />
      <PeoplePickerCollapsibleChecklist label="Task Owner" dataKey="taskOwner" />
      <PeoplePickerCollapsibleChecklist label="Task Assignee" dataKey="taskAssignee" />
      <GenericCollapsibleChecklist
        label="Primary Blocker Category"
        dataKey="primaryBlockerCategoryIds"
        query={GET_LEGO_CATEGORIES_QUERY}
        mapData={mapLegoCategories}
        showSearch={false}
      />
    </ListFilters>
  );
};

export default MonitoringQueueFilters;
